const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';

const ACCESS_KEYS = ['userToken', 'user_token'] as const;
const REFRESH_KEYS = ['userRefreshToken', 'user_refresh_token'] as const;

let refreshInFlight: Promise<string | null> | null = null;

const getFirstLocalStorageValue = (keys: readonly string[]) => {
    for (const key of keys) {
        const value = localStorage.getItem(key);
        if (value) {
            return value;
        }
    }
    return null;
};

export const getUserAccessToken = () => getFirstLocalStorageValue(ACCESS_KEYS);

export const getUserRefreshToken = () => getFirstLocalStorageValue(REFRESH_KEYS);

export const setUserTokens = (accessToken: string, refreshToken?: string | null) => {
    localStorage.setItem('userToken', accessToken);

    if (refreshToken && refreshToken.trim()) {
        localStorage.setItem('userRefreshToken', refreshToken);
    }
};

export const clearUserSession = () => {
    for (const key of ACCESS_KEYS) {
        localStorage.removeItem(key);
    }
    for (const key of REFRESH_KEYS) {
        localStorage.removeItem(key);
    }

    localStorage.removeItem('userInfo');
    localStorage.removeItem('agencyToken');
    localStorage.removeItem('agencyInfo');
    localStorage.removeItem('principalInfo');
    localStorage.removeItem('marketingInfo');
    localStorage.removeItem('inquiryInfo');
    localStorage.removeItem('informationInfo');
    localStorage.removeItem('transmittalInfo');
};

const extractTokenValue = (payload: unknown, keys: string[]): string | null => {
    if (!payload || typeof payload !== 'object') {
        return null;
    }

    const source = payload as Record<string, unknown>;
    const nested = source.data && typeof source.data === 'object' ? (source.data as Record<string, unknown>) : null;

    for (const key of keys) {
        const direct = source[key];
        if (typeof direct === 'string' && direct.trim()) {
            return direct;
        }

        const nestedValue = nested?.[key];
        if (typeof nestedValue === 'string' && nestedValue.trim()) {
            return nestedValue;
        }
    }

    return null;
};

export const extractUserAccessToken = (payload: unknown) =>
    extractTokenValue(payload, ['token', 'access_token', 'accessToken']);

export const extractUserRefreshToken = (payload: unknown) =>
    extractTokenValue(payload, ['refresh_token', 'refreshToken']);

export const refreshUserAccessToken = async (): Promise<string | null> => {
    const refreshToken = getUserRefreshToken();
    if (!refreshToken) {
        return null;
    }

    if (refreshInFlight) {
        return refreshInFlight;
    }

    refreshInFlight = (async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/user/refresh`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    refresh_token: refreshToken,
                    refreshToken,
                    token: refreshToken,
                }),
            });

            if (!response.ok) {
                return null;
            }

            const data = await response.json().catch(() => ({}));
            const nextAccessToken = extractUserAccessToken(data);
            const nextRefreshToken = extractUserRefreshToken(data);

            if (!nextAccessToken) {
                return null;
            }

            setUserTokens(nextAccessToken, nextRefreshToken);
            return nextAccessToken;
        } catch {
            return null;
        } finally {
            refreshInFlight = null;
        }
    })();

    return refreshInFlight;
};

export const isUnauthorizedResponse = (response: Response) => response.status === 401 || response.status === 403;

export const userFetch = async (
    input: RequestInfo | URL,
    init: RequestInit = {},
    options: { retryOnAuthFailure?: boolean } = {}
): Promise<Response> => {
    const { retryOnAuthFailure = true } = options;

    const accessToken = getUserAccessToken();
    const headers = new Headers(init.headers || {});
    if (accessToken && !headers.has('Authorization')) {
        headers.set('Authorization', `Bearer ${accessToken}`);
    }

    const firstResponse = await fetch(input, {
        ...init,
        credentials: init.credentials ?? 'include',
        headers,
    });

    if (!retryOnAuthFailure || !isUnauthorizedResponse(firstResponse)) {
        return firstResponse;
    }

    const refreshedAccessToken = await refreshUserAccessToken();
    if (!refreshedAccessToken) {
        return firstResponse;
    }

    const retryHeaders = new Headers(init.headers || {});
    retryHeaders.set('Authorization', `Bearer ${refreshedAccessToken}`);

    return fetch(input, {
        ...init,
        credentials: init.credentials ?? 'include',
        headers: retryHeaders,
    });
};
